// Shared trend line-chart helpers — lab reference thresholds + abnormal-point styling.
// Used by BOTH the Assistant chart and the patient Clinical Summary trends so the two render
// identically. (Extracted from the Assistant chart; see PD-978 chart unification.)

// Keys the value of a point's reference bounds. Lab and vital points copy them from the reading's
// own document; a null bound draws no line, and charts without any bound render as a plain line.
export const REFERENCE_HIGH_KEY = 'referenceHigh'
export const REFERENCE_LOW_KEY = 'referenceLow'

export type AbnormalFlag =
  | 'high'
  | 'low'
  | 'critical-high'
  | 'critical-low'
  | 'abnormal'
  | null
  | undefined

// Narrow view of a data point's lab fields. We cast to this rather than rely on the wire type's
// inferred shape so the renderer is robust to how the field types propagate through oRPC.
export type LabDatum = {
  referenceLow?: number | null
  referenceHigh?: number | null
  abnormalFlag?: AbnormalFlag
  // Set on a qualified lab reading ('<5'): `value` is the reported limit, not an exact number.
  comparator?: '<' | '<=' | '>=' | '>' | null
}

export type ChartDatum = Record<string, unknown>

// Out-of-range points get both a colour and a shape (accessible without relying on colour alone):
// high ▲ red, low ▼ indigo, criticals a darker shade, the non-directional `abnormal` an orange
// diamond, in-range a black circle (matching the value line).
export type DotShape = 'triangle-up' | 'triangle-down' | 'diamond' | 'circle'

export const dotStyleFor = (
  flag: AbnormalFlag,
): { shape: DotShape; color: string; radius: number } => {
  switch (flag) {
    case 'critical-high':
      return { shape: 'triangle-up', color: 'var(--mantine-color-red-8)', radius: 5 }
    case 'high':
      return { shape: 'triangle-up', color: 'var(--mantine-color-red-6)', radius: 5 }
    case 'critical-low':
      return { shape: 'triangle-down', color: 'var(--mantine-color-indigo-8)', radius: 5 }
    case 'low':
      return { shape: 'triangle-down', color: 'var(--mantine-color-indigo-6)', radius: 5 }
    case 'abnormal':
      return { shape: 'diamond', color: 'var(--mantine-color-orange-6)', radius: 5 }
    default:
      return { shape: 'circle', color: 'var(--mantine-color-black)', radius: 3 }
  }
}

// A set of points carries "lab signals" when any point has a reference bound or an abnormal flag —
// only then do we draw threshold lines and colour the points.
export const hasLabSignals = (data: ReadonlyArray<ChartDatum>): boolean =>
  data.some((datum) => {
    const d = datum as LabDatum
    return (
      typeof d.referenceHigh === 'number' ||
      typeof d.referenceLow === 'number' ||
      d.abnormalFlag != null
    )
  })

// Whether any point is out of range — drives the "Abnormal" badge.
export const hasAbnormalPoints = (data: ReadonlyArray<ChartDatum>): boolean =>
  data.some((datum) => (datum as LabDatum).abnormalFlag != null)

export const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null

  const parsed = Number(value.replace(/[,%\s]/g, ''))
  return Number.isFinite(parsed) ? parsed : null
}

export const toLabel = (value: unknown, fallbackIndex: number): string => {
  if (typeof value === 'string' && value.trim()) return value
  if (typeof value === 'number') return String(value)
  if (typeof value === 'boolean') return value ? 'True' : 'False'
  return `Item ${fallbackIndex + 1}`
}

export const formatChartNumber = (value: number): string => {
  if (!Number.isFinite(value)) return '-'
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value)
}

// A reading as the lab reported it: a qualified reading keeps its comparator and its full reported
// limit ('<0.001', never rounded to '<0'); an exact one prints as the plain number.
const fullPrecision = new Intl.NumberFormat('en-US', { maximumFractionDigits: 20 })
export const formatChartReading = (value: number, comparator?: LabDatum['comparator']): string =>
  comparator && Number.isFinite(value)
    ? `${comparator}${fullPrecision.format(value)}`
    : formatChartNumber(value)

// Keep only numeric-y points, normalize the x label, and preserve lab fields (reference bounds /
// abnormal flag) so the renderer can draw thresholds and colour the points.
export const normalizeCartesianData = (
  data: ReadonlyArray<ChartDatum>,
  xKey: string,
  yKey: string,
): ChartDatum[] =>
  data.flatMap<ChartDatum>((datum, index) => {
    const value = toNumber(datum[yKey])
    if (value === null) return []

    return {
      ...datum,
      [xKey]: toLabel(datum[xKey], index),
      [yKey]: value,
    }
  })

// Parse a date-ish value to epoch milliseconds; null when it isn't a usable date.
export const toEpochMs = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const t = Date.parse(value)
  return Number.isNaN(t) ? null : t
}

// Like normalizeCartesianData, but the x becomes epoch ms (for a numeric time axis) and points are
// sorted chronologically. Points whose x isn't a parseable date or whose y isn't numeric are dropped.
export const normalizeTimeData = (
  data: ReadonlyArray<ChartDatum>,
  xKey: string,
  yKey: string,
): ChartDatum[] =>
  data
    .flatMap<ChartDatum>((datum) => {
      const value = toNumber(datum[yKey])
      const ms = toEpochMs(datum[xKey])
      if (value === null || ms === null) return []
      return { ...datum, [xKey]: ms, [yKey]: value }
    })
    .sort((a, b) => (a[xKey] as number) - (b[xKey] as number))

// True when every point's x is a date-like string (parseable, with a 4-digit year) and there are at
// least two points — i.e. safe to place on a time axis without dropping points or misreading a
// plain numeric/categorical x (e.g. a dose or a count) as epoch milliseconds.
export const isTimeAxisData = (data: ReadonlyArray<ChartDatum>, xKey: string): boolean => {
  let n = 0
  for (const datum of data) {
    const v = datum[xKey]
    if (typeof v !== 'string' || !/\d{4}/.test(v) || Number.isNaN(Date.parse(v))) return false
    n++
  }
  return n >= 2
}

// US-format MM/DD/YYYY in UTC — matches the day bucketing used elsewhere and avoids timezone
// drift on the axis.
export const formatUtcDate = (ms: number): string => {
  const d = new Date(ms)
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(d.getUTCDate()).padStart(2, '0')
  return `${mm}/${dd}/${d.getUTCFullYear()}`
}

// Round to the 2 most significant digits, directionally: 'floor' never rounds up, 'ceil' never
// rounds down — so a domain bound only ever widens. E.g. 94.7 floors to 94; 110.3 ceils to 120;
// 1.28 ceils to 1.3. The epsilon absorbs float noise from the division so an exactly-representable
// bound (0.55 → 55) doesn't slip a step.
const roundToTwoSignificant = (value: number, mode: 'floor' | 'ceil'): number => {
  if (value === 0) return 0
  const exponent = Math.floor(Math.log10(Math.abs(value))) - 1
  const magnitude = Math.pow(10, exponent)
  const scaled = value / magnitude
  const rounded = mode === 'floor' ? Math.floor(scaled + 1e-9) : Math.ceil(scaled - 1e-9)
  return Number((rounded * magnitude).toPrecision(12))
}

// Visible y-range for a trend chart: the plotted extremes (the value series plus any reference
// threshold lines, so the dashed bounds never clip) padded by 10% of the span on each side, then
// rounded outward to 2 significant digits (floor the bottom, ceil the top) so the axis ticks land
// on clean numbers. Recharts' default domain anchors at 0, which flattens series whose values sit
// far from zero (e.g. sodium ~140). A flat series pads by 10% of the value (else 1) so the line
// stays centred.
export const paddedYDomain = (
  data: ReadonlyArray<ChartDatum>,
  yKey: string,
): [number, number] | undefined => {
  const values: number[] = []
  for (const datum of data) {
    for (const key of [yKey, REFERENCE_LOW_KEY, REFERENCE_HIGH_KEY]) {
      const value = datum[key]
      if (typeof value === 'number' && Number.isFinite(value)) values.push(value)
    }
  }
  if (values.length === 0) return undefined
  const min = Math.min(...values)
  const max = Math.max(...values)
  const pad = (max - min) * 0.1 || Math.abs(max) * 0.1 || 1
  // Padding must not push the floor below zero when nothing plotted is negative — a negative
  // axis start on an all-positive measurement reads as if negative values were possible.
  const lower = min >= 0 ? Math.max(0, min - pad) : min - pad
  return [roundToTwoSignificant(lower, 'floor'), roundToTwoSignificant(max + pad, 'ceil')]
}
